import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { ModalService } from '../../../core/services/modal.service';
import { PrivateBookingMainFormComponent } from '../../components/private-booking-main-form/private-booking-main-form.component';
import { Store } from '@ngrx/store';
import { roomsActions } from '../../../core/store/rooms';
import { authActions } from '../../../core/store/auth';
import { BookingPermission } from 'angular/app/core/models/entities/permission';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { fromStore } from 'angular/app/core/store';
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  skipWhile,
  take,
  takeUntil,
} from 'rxjs';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { User } from '../../../core/models/entities/user';
import { communicationTemplatesActions } from 'angular/app/core/store/communication-templates';
import {
  BookingsDisplayMode,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import { parseISO } from 'date-fns';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { PersonalBookingsModalComponent } from '../../components/personal-bookings-modal/personal-bookings-modal.component';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import {
  selectAuthState,
  selectAuthUser,
  selectBookingsDisplayMode,
  selectBookingsRoomsFilter,
} from '../../../core/store/reducers';
import { BookingCalendarNavigationService } from '../../../core/services/booking-calendar-navigation.service';
import { ExportExcelModalComponent } from '../../components/export-excel-modal/export-excel-modal.component';
import { BookingDetailsModalComponent } from '../../components/booking-details-modal/booking-details-modal.component';

const VIEW_QUERY_PARAM = 'view';

@Component({
  selector: 'app-bookings-page',
  template: `
    <app-page-header [title]="'nav.bookings' | transloco">
      <!-- Actions template -->
      <ng-template #actions>
        @if (canExportBookings() || canImportBookings()) {
          <app-dropdown>
            <app-button variant="outlined" dropdownButton type="button">
              <i class="fas fa-ellipsis-h"></i>
            </app-button>
            @if (canExportBookings()) {
              <button
                dropdownAction
                type="button"
                (click)="openExportExcelModal()"
              >
                {{ 'bookings.page.export-button' | transloco }}
              </button>
            }
            @if (canImportBookings()) {
              <button dropdownAction type="button" routerLink="import">
                {{ 'bookings.page.import-button' | transloco }}
              </button>
            }
          </app-dropdown>
        }

        @if (hasPrivateRoom$ | async) {
          <app-button variant="outlined" (click)="openMyBookingsModal()">
            {{ 'bookings.page.my-bookings-button' | transloco }}
          </app-button>
        }

        <ng-template
          [appHasPermission]="BookingPermission.StorePublic"
          #createBookingDropdown
        >
          @if (
            (hasPrivateRoom$ | async) === true ||
            (hasPublicRoom$ | async) === true
          ) {
            <app-dropdown>
              <app-button dropdownButton id="add-booking"
                >{{ 'bookings.page.add-booking-button' | transloco }}</app-button
              >
              @if (hasPrivateRoom$ | async) {
                <button
                  dropdownAction
                  type="button"
                  data-cy="internal-booking-action"
                  (click)="createPrivateBooking()"
                >
                  {{ 'common.bookable-by.PRIVATE' | transloco }}
                </button>
              }
              @if (hasPublicRoom$ | async) {
                <button
                  dropdownAction
                  type="button"
                  data-cy="external-booking-action"
                  (click)="createPublicBooking()"
                >
                  {{ 'bookings.page.external-booking-action' | transloco }}
                </button>
              }
            </app-dropdown>
          }
        </ng-template>
        <ng-template [appHasNoPermission]="BookingPermission.StorePublic">
          <app-button id="add-private-booking" (click)="createPrivateBooking()">
            {{ 'common.button.add' | transloco }}
          </app-button>
        </ng-template>
      </ng-template>
    </app-page-header>

    <app-spacer></app-spacer>

    <ng-template [appHasPermission]="BookingPermission.Update">
      <app-pending-bookings></app-pending-bookings>
    </ng-template>

    <app-bookings-list [scope]="BookingsScope.All"></app-bookings-list>
  `,
  standalone: false,
})
export class BookingsPageComponent
  extends DestroyableComponent
  implements OnInit
{
  private translocoService = inject(TranslocoService);

  BookingPermission = BookingPermission;
  BookingsScope = BookingsScope;
  hasPublicRoom$ = this.store
    .select(fromStore.selectAllRooms)
    .pipe(
      map((rooms) =>
        rooms.some(
          (room) => room.active && room.bookable_by !== BookableBy.Private,
        ),
      ),
    );
  hasPrivateRoom$ = this.store
    .select(fromStore.selectAllRooms)
    .pipe(
      map((rooms) =>
        rooms.some(
          (room) => room.active && room.bookable_by !== BookableBy.Public,
        ),
      ),
    );
  loggedUser$ = this.store.select(selectAuthUser);
  private authUser = this.store.selectSignal(selectAuthUser);
  canExportBookings = computed(
    () =>
      this.authUser()?.all_permissions?.includes(BookingPermission.Export) ??
      false,
  );
  canImportBookings = computed(
    () =>
      this.authUser()?.all_permissions?.includes(BookingPermission.Import) ??
      false,
  );
  exportingData = signal(false);
  private currentDisplayMode = this.store.selectSignal(
    selectBookingsDisplayMode(BookingsScope.All),
  );

  constructor(
    private modalService: ModalService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private bookingCalendarNavigationService: BookingCalendarNavigationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loggedUser$
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged((a, b) => a?.vendor?.id === b?.vendor?.id),
      )
      .subscribe((user) => {
        this.store.dispatch(roomsActions.findAll({ page: 1 }));
        this.store.dispatch(communicationTemplatesActions.findAll());
      });

    this.refreshPendingBookingsOnNavigation();
    this.syncDisplayModeWithUrl();

    if (this.route.snapshot.queryParams['date']) {
      this.store.dispatch(
        bookingsActions.setDisplayMode({
          displayMode: BookingsDisplayMode.Week,
          scope: BookingsScope.All,
        }),
      );
      this.store.dispatch(
        bookingsActions.setCalendarDate({
          date: parseISO(this.route.snapshot.queryParams['date']),
          scope: BookingsScope.All,
        }),
      );
    }

    this.openBookingFromQueryParams();
  }

  private async openBookingFromQueryParams(): Promise<void> {
    const bookingIdParam = this.route.snapshot.queryParams['booking'];
    if (!bookingIdParam) {
      return;
    }

    await this.switchVendorIfNeeded(this.route.snapshot.queryParams['vendor']);

    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    const modal = instance.componentRef.instance as BookingDetailsModalComponent;
    modal.bookingId = Number(bookingIdParam);
    modal.displayCalendarButton = true;

    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam) {
      modal.defaultTab = tabParam;
    }

    this.clearDeepLinkQueryParams();
  }

  private clearDeepLinkQueryParams(): void {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { booking: null, tab: null, vendor: null },
      queryParamsHandling: 'merge',
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  private async switchVendorIfNeeded(
    vendorParam: string | undefined,
  ): Promise<void> {
    if (!vendorParam) {
      return;
    }

    const targetVendorId = Number(vendorParam);
    const user = await firstValueFrom(
      this.loggedUser$.pipe(filter(Boolean), take(1)),
    );
    if (user.vendor?.id === targetVendorId) {
      return;
    }

    const targetVendor = user.vendors?.find(
      (vendor) => vendor.id === targetVendorId,
    );
    if (!targetVendor) {
      return;
    }

    this.store.dispatch(authActions.setVendor({ vendor: targetVendor }));
    this.store.dispatch(bookingsActions.resetFilters());
    this.store.dispatch(authActions.findCurrentUser());
    await firstValueFrom(
      this.store
        .select(selectAuthState)
        .pipe(
          skipWhile((state) => state.loading),
          take(1),
        ),
    );
  }

  createPublicBooking() {
    this.store
      .select(fromStore.selectAuthUser)
      .pipe(take(1))
      .subscribe((user: User) => {
        this.router.navigate(['/louer-salle/', user.vendor.slug]);
      });
  }

  createPrivateBooking() {
    const instance = this.modalService.open(PrivateBookingMainFormComponent, {
      title: this.translocoService.translate('bookings.page.create-private-booking-title'),
      size: 'screen',
    });
    const component = instance.componentRef
      .instance as PrivateBookingMainFormComponent;

    const roomsFilter = this.store.selectSignal(
      selectBookingsRoomsFilter(BookingsScope.All),
    )();
    if (roomsFilter?.ids?.length === 1) {
      component.preselectRoom(roomsFilter.ids[0]);
    }

    outputToObservable(component.bookingSaved)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((startDate) =>
        this.bookingCalendarNavigationService.handlePrivateBookingSaved(startDate),
      );
  }

  openMyBookingsModal() {
    const instance = this.modalService.open(PersonalBookingsModalComponent, {
      size: 'screen',
      title: this.translocoService.translate('bookings.page.my-bookings-modal-title'),
      showActionButtons: false,
    });
  }

  openExportExcelModal() {
    const modalRef = this.modalService.open(ExportExcelModalComponent, {});
  }

  private syncDisplayModeWithUrl(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => this.displayModeFromUrl(params.get(VIEW_QUERY_PARAM))),
        filter((displayMode) => displayMode !== null),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe((displayMode) => {
        if (displayMode !== this.currentDisplayMode()) {
          this.store.dispatch(
            bookingsActions.setDisplayMode({
              displayMode,
              scope: BookingsScope.All,
            }),
          );
        }
      });

    this.store
      .select(selectBookingsDisplayMode(BookingsScope.All))
      .pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((displayMode) => this.writeDisplayModeToUrl(displayMode));
  }

  private writeDisplayModeToUrl(displayMode: BookingsDisplayMode): void {
    const urlView = this.route.snapshot.queryParamMap.get(VIEW_QUERY_PARAM);
    if (this.displayModeFromUrl(urlView) === displayMode) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [VIEW_QUERY_PARAM]: displayMode.toLowerCase() },
      queryParamsHandling: 'merge',
      replaceUrl: urlView === null,
    });
  }

  private displayModeFromUrl(view: string | null): BookingsDisplayMode | null {
    const displayMode = view?.toUpperCase() as BookingsDisplayMode;
    return Object.values(BookingsDisplayMode).includes(displayMode)
      ? displayMode
      : null;
  }

  private refreshPendingBookingsOnNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => this.store.dispatch(bookingsActions.findAllPending()));
  }
}
